// Poll — craftjs Build-Menu block (NEW, Premium). Fable Hail Mary Wave 5
// (Lane-N, BLOCK-FORMS). Deck: concepts/builder-blocks/03-forms-poll-block +
// 04-viewwheel-poll-live. The block renders a live poll (question + options +
// vote button + results bars); the Settings slot bridges the node props to the
// design-system PollEditor (BlockEditorPane framework).
//
// PREMIUM gate: purely SERVER-enforced & fail-closed (BE: vote submission +
// results are rejected unless the WHEEL OWNER's tier >= Premium). The authoring
// surface here is unflagged (Gordon's flag policy).
//
// LIVE voting (Bucket-D Wave-D2): the builder always shows a local preview tally.
// In the published view (craft Editor enabled === false) with a wheel in the URL,
// and only when POLL_LIVE_VOTING_ENABLED is ON, the block posts real votes to
// /wheel/forms/poll/<wheel_id>/<block_id>/{vote,results}/ (services/pollBlocks) —
// one vote per user/session enforced server-side. Flag OFF (default) keeps the
// block byte-identical to today (preview tally, no network). See featureFlags.js.

import { useNode, useEditor } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import PollEditor, { POLL_DEFAULT } from "../../BlockEditorPane/editors/PollEditor";
import { POLL_LIVE_VOTING_ENABLED } from "../../../../constants/featureFlags";
import { castPollVote, getPollResults } from "../../../../services/pollBlocks";
import { useSegmentContext } from "../../../../pages/ViewWheel/SegmentContext";
import { pollQuestionTone } from "./pollQuestionTone";
import {
  parseResults,
  percentFor,
  buildVotePayload,
  hasVotedMarker,
  setVotedMarker,
} from "./pollLive";

const VOTE_BTN = "#8F38EF"; // deck: Vote button colour

export const Poll = ({ question, options, optionColor, allowMultiple, showResults, live, margin }) => {
  const {
    connectors: { connect, drag },
    id: blockId,
  } = useNode((node) => ({ id: node.id }));
  // Published view = craft Editor rendered with enabled={false} (ViewWheel).
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const { wheelId } = useParams();
  const selectedArc = useSegmentContext();
  const questionTone = pollQuestionTone(selectedArc?.settings);

  const opts = options?.length ? options : POLL_DEFAULT.options;
  const [selected, setSelected] = useState([]);
  const [voted, setVoted] = useState(false);

  // LIVE voting only turns on in the published view (Editor disabled) with a wheel
  // in the URL, and only when the flag is ON. Otherwise the block is BYTE-IDENTICAL
  // to today: local preview tally, no network. (`live` prop kept for back-compat.)
  const isLive =
    POLL_LIVE_VOTING_ENABLED && enabled === false && !!wheelId && !!blockId;
  const [results, setResults] = useState({ tallies: {}, total: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [premiumLocked, setPremiumLocked] = useState(false);
  const [error, setError] = useState("");
  const storage = typeof window !== "undefined" ? window.localStorage : null;

  // Entering the live view: if the visitor already voted this session, or the poll
  // shows results up-front, load the real standings from the shared endpoint.
  useEffect(() => {
    if (!isLive) return;
    const already = hasVotedMarker(storage, wheelId, blockId);
    if (already) setVoted(true);
    if (already || showResults) {
      getPollResults(wheelId, blockId)
        .then((res) => setResults(parseResults(res?.data)))
        .catch((e) => {
          if (e?.response?.status === 403) setPremiumLocked(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, wheelId, blockId, showResults]);

  const toggle = (id) => {
    if (voted || submitting) return;
    setError("");
    setSelected((prev) => {
      if (allowMultiple) return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      return [id];
    });
  };

  const submitLiveVote = async () => {
    if (submitting || !selected.length) return;
    const { optionIds, allowMultiple: multi } = buildVotePayload(selected, allowMultiple);
    setSubmitting(true);
    setError("");
    try {
      const res = await castPollVote(wheelId, blockId, { optionIds, allowMultiple: multi });
      setResults(parseResults(res?.data));
      setVoted(true);
      setVotedMarker(storage, wheelId, blockId);
    } catch (e) {
      if (e?.response?.status === 403) setPremiumLocked(true);
      else setError("Your vote could not be recorded. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = () => {
    if (!selected.length) return;
    if (isLive) submitLiveVote();
    else setVoted(true); // builder / preview — local only (unchanged)
  };

  // Percent per option: real tallies when live, else the original preview formula.
  const tally = opts.map((o, i) => ({
    ...o,
    pct: isLive
      ? percentFor(results.tallies, results.total, o.id)
      : voted
      ? selected.includes(o.id)
        ? Math.max(20, 60 - i * 8)
        : Math.max(6, 24 - i * 5)
      : 0,
  }));
  const showBars = isLive
    ? (voted || showResults) && !premiumLocked
    : voted && showResults;

  const marginStyle = margin
    ? `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`
    : undefined;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div style={{ padding: marginStyle }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 4 }}>
            {question ? (
              <div
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  color: questionTone.color,
                  textShadow: questionTone.textShadow,
                }}
              >
                {question}
              </div>
            ) : (
              <div style={{ fontFamily: "DM Sans, sans-serif", fontSize: 15, fontWeight: 700, color: "#98A2B3" }}>
                Your poll question…
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tally.map((o) => {
                const isSel = selected.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-pressed={isSel}
                    disabled={submitting || premiumLocked}
                    onClick={() => toggle(o.id)}
                    style={{
                      position: "relative",
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: `1.5px solid ${isSel ? optionColor : "#E4E7EC"}`,
                      background: isSel ? `${optionColor}14` : "#fff",
                      color: "#123051",
                      fontFamily: "DM Sans, sans-serif",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: voted || submitting || premiumLocked ? "default" : "pointer",
                      overflow: "hidden",
                    }}
                  >
                    {showBars && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: `${o.pct}%`,
                          background: `${optionColor}22`,
                          transition: "width .3s ease",
                        }}
                      />
                    )}
                    <span style={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
                      <span>{o.text || "Option"}</span>
                      {showBars && <span style={{ color: optionColor }}>{o.pct}%</span>}
                    </span>
                  </button>
                );
              })}
            </div>
            {!voted && !premiumLocked && (
              <button
                type="button"
                disabled={!selected.length || submitting}
                onClick={handleVote}
                style={{
                  alignSelf: "flex-end",
                  padding: "10px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: selected.length ? VOTE_BTN : "#CBB4FE",
                  color: "#fff",
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: selected.length && !submitting ? "pointer" : "not-allowed",
                }}
              >
                {submitting ? "Submitting…" : "Vote"}
              </button>
            )}
            {isLive && premiumLocked && (
              <div style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: "#667085" }}>
                Voting is available on this creator&apos;s Premium plan.
              </div>
            )}
            {isLive && error && !premiumLocked && (
              <div
                role="alert"
                style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: "#D9480F" }}
              >
                {error}
              </div>
            )}
            {voted && (
              <div style={{ fontFamily: "DM Sans, sans-serif", fontSize: 12, color: "#667085" }}>
                Thanks — you voted.
                {isLive && results.total
                  ? ` (${results.total} vote${results.total === 1 ? "" : "s"})`
                  : ""}
              </div>
            )}
          </div>
        </div>
      </BlockIndicator>
    </div>
  );
};

const PollSettings = () => {
  const {
    actions: { setProp },
    question,
    options,
    optionColor,
    allowMultiple,
    showResults,
    margin,
  } = useNode((node) => ({
    question: node.data.props.question,
    options: node.data.props.options,
    optionColor: node.data.props.optionColor,
    allowMultiple: node.data.props.allowMultiple,
    showResults: node.data.props.showResults,
    margin: node.data.props.margin,
  }));

  const value = {
    question: question ?? POLL_DEFAULT.question,
    options: options ?? POLL_DEFAULT.options,
    optionColor: optionColor ?? POLL_DEFAULT.optionColor,
    allowMultiple: allowMultiple ?? POLL_DEFAULT.allowMultiple,
    showResults: showResults ?? POLL_DEFAULT.showResults,
    margin: margin ?? POLL_DEFAULT.margin,
  };

  return (
    <PollEditor
      value={value}
      onChange={(next) =>
        setProp((props) => {
          props.question = next.question;
          props.options = next.options;
          props.optionColor = next.optionColor;
          props.allowMultiple = next.allowMultiple;
          props.showResults = next.showResults;
          props.margin = next.margin;
        })
      }
    />
  );
};

export const PollDefaultProps = { ...POLL_DEFAULT };

Poll.craft = {
  props: PollDefaultProps,
  related: {
    settings: PollSettings,
  },
  displayName: "PollWidget",
};

export default Poll;
